import { useEffect, useRef, useState, type ReactNode, type Ref } from "react";
import { useFitToScreen } from "./useFitToScreen";
import { usePinnedHeader } from "./usePinnedHeader";
import { Minimize2 } from "lucide-react";

import { Button, Skeleton } from "@nexus/ui";
import { cn } from "@/lib/utils";
import { AppLayout } from "./AppLayout";
import { Logo } from "./Logo";
import { PageScopeControls } from "./PageScopeControls";
import type { PageHeaderControls } from "./PageHeader";
import { PresentationHeaderContext, usePageHeaderHost } from "./PresentationHeaderSlots";

/**
 * Chrome-free, distance-readable page for a war-room TV: title and
 * description on the left, the brand logo on the right, and an exit control
 * that appears on pointer movement. Everything stays inside a safe area
 * (`--presentation-safe-*`): TVs may crop the outer edge of the picture
 * (overscan), and a header with room above and below reads as the page's title
 * rather than a strip squeezed against the widgets.
 *
 * A dashboard whose pages name themselves in the header (Page header or Side
 * list navigation, see `PresentationHeaderSlots`) shows the dashboard and its
 * page side by side in one header, two identities built alike and split by a
 * hairline: image, title, and a line under it — the dashboard's scope (a map)
 * with its context, then the page's subject (a flag) with its position, next
 * page and controls. The logo keeps its room at the right. Tabs keep the
 * dashboard header, with the tabs under it.
 *
 * On the right, the refresh status precedes the controls on the title's line,
 * without a separator. The controls wrap together when space is limited,
 * independently of the dashboard's and page's identities.
 */
export function PresentationLayout({
  title,
  titleLoading = false,
  description,
  titleVisual,
  status,
  tenantScopeRail,
  filters,
  previewFilters,
  viewActions,
  contextRef,
  statusRef,
  onExit,
  children,
}: {
  title: string;
  titleLoading?: boolean;
  description?: ReactNode;
  tenantScopeRail?: boolean;
  contextRef?: Ref<HTMLDivElement>;
  onExit: () => void;
  children: ReactNode;
} & Pick<
  PageHeaderControls,
  "filters" | "previewFilters" | "viewActions" | "titleVisual" | "status" | "statusRef"
>) {
  const [exitVisible, setExitVisible] = useState(false);
  const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mainRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  usePinnedHeader(headerRef, true);
  const { paged, context: headerContext, refs } = usePageHeaderHost("presentation");
  useFitToScreen(mainRef, contentRef);
  useEffect(
    () => () => {
      if (exitTimer.current) clearTimeout(exitTimer.current);
    },
    [],
  );

  function revealExit() {
    setExitVisible(true);
    if (exitTimer.current) clearTimeout(exitTimer.current);
    exitTimer.current = setTimeout(() => setExitVisible(false), 2200);
  }

  /** Dots between the items of a context line. */
  const separated =
    "[&>*+*]:relative [&>*+*]:pl-3 [&>*+*]:before:absolute [&>*+*]:before:top-1/2 [&>*+*]:before:left-0 [&>*+*]:before:size-[3px] [&>*+*]:before:-translate-y-1/2 [&>*+*]:before:rounded-full [&>*+*]:before:bg-current [&>*+*]:before:opacity-100";
  const titleText = titleLoading ? (
    <>
      <span className="sr-only">{title}</span>
      <Skeleton className="my-[0.1em] h-[1em] w-64 max-w-full" />
    </>
  ) : (
    title
  );
  const context = (
    <>
      {description ? (
        <div
          className="min-w-0 max-w-full truncate"
          title={typeof description === "string" ? description : undefined}
        >
          {description}
        </div>
      ) : null}
      {contextRef ? (
        <div ref={contextRef} className="min-w-0 overflow-hidden" />
      ) : (
        <>
          <PageScopeControls
            slot="tenant-context"
            previewFilters={previewFilters}
            description={description}
          />
          <PageScopeControls slot="period" previewFilters={previewFilters} />
        </>
      )}
    </>
  );
  const controls = (
    <div data-presentation-side className="flex min-w-0 max-w-full flex-wrap items-center justify-end gap-x-(--presentation-header-gap) gap-y-2">
      <div
        ref={statusRef}
        data-header-status
        className="flex min-h-[calc(var(--presentation-title-size)*1.2)] shrink-0 items-center text-(length:--presentation-body-size) leading-6 text-secondary empty:hidden"
      >
        {status}
      </div>
      <div className="flex min-h-[calc(var(--presentation-title-size)*1.2)] min-w-0 max-w-full flex-wrap items-center justify-end gap-x-3 gap-y-2">
        <div
          ref={refs.controls}
          data-header-page-controls
          className="flex shrink-0 items-center empty:hidden"
        />
        <span
          aria-hidden
          className="hidden h-[calc(var(--presentation-title-size)*0.9)] w-px shrink-0 bg-border-strong/70 [[data-header-page-controls]:not(:empty)~&:has(~[data-presentation-scope]:not(:empty))]:block"
        />
        <div
          data-presentation-scope
          className="flex min-w-0 max-w-full flex-wrap items-center justify-end gap-2 empty:hidden"
        >
          <PageScopeControls slot="tenant" previewFilters={previewFilters} />
          {filters}
          <PageScopeControls slot="event" previewFilters={previewFilters} />
          <PageScopeControls slot="time" previewFilters={previewFilters} />
          {viewActions}
        </div>
      </div>
    </div>
  );
  const logo = (
    <Logo variant="full" size={72} className="h-(--presentation-logo-height)! shrink-0" />
  );
  const exit = (
    <Button
      variant="ghost"
      size="sm"
      onClick={onExit}
      aria-keyshortcuts="Escape"
      title="Exit fullscreen (Esc)"
      className={cn(
        "fixed right-(--presentation-safe-x) bottom-(--presentation-safe-bottom) z-50 bg-surface/95 shadow-sm transition-opacity duration-300 focus-visible:opacity-100 [@media(hover:none)]:opacity-100",
        exitVisible ? "opacity-100" : "opacity-0",
      )}
    >
      <Minimize2 className="size-4" aria-hidden="true" />
      Exit fullscreen
    </Button>
  );

  const header = paged ? (
    <header
      ref={headerRef}
      data-page-identity
      className="@container/pageheader sticky top-0 z-30 mx-(--presentation-safe-x) flex min-w-0 flex-wrap items-center gap-x-(--presentation-header-gap) gap-y-3 bg-background pt-(--presentation-safe-top) pb-(--presentation-header-bottom)"
    >
      {/* The dashboard: its scope (a map), its title and its context. */}
      <div
        data-presentation-dashboard
        className="flex min-w-0 shrink items-center gap-x-4 basis-auto max-w-[48%]"
      >
        {titleVisual ? (
          <div
            data-header-image
            className="flex h-(--presentation-title-block) max-w-[calc(var(--presentation-title-block)*2)] shrink-0 items-center"
          >
            {titleVisual}
          </div>
        ) : null}
        <div className="min-w-0">
          <h1
            aria-busy={titleLoading || undefined}
            className="truncate text-(length:--presentation-title-size) leading-[1.2] font-semibold text-brand-navy dark:text-white"
          >
            {titleText}
          </h1>
          <div
            className={cn(
              "mt-1.5 flex min-h-6 min-w-0 items-center gap-x-2 text-(length:--presentation-body-size) leading-6 text-secondary",
              separated,
            )}
          >
            {context}
          </div>
        </div>
      </div>
      <span
        aria-hidden
        data-presentation-separator
        className="h-(--presentation-title-block) w-px shrink-0 bg-border-strong/70"
      />
      {/* The page: its subject (a flag), its name, its position and controls. It clips at
          the separator, so a new page seems to come out of it (see PresentationPageIdentity). */}
      {/* Never squeezed to nothing by the controls: the dashboard's title truncates first. */}
      <div
        data-presentation-page
        className="flex min-w-[clamp(10rem,24%,20rem)] flex-1 items-center gap-x-4 overflow-x-clip [overflow-clip-margin:0.75rem]"
      >
        <div
          ref={refs.mark}
          className="relative flex h-(--presentation-title-block) shrink-0 empty:hidden"
        />
        <div className="min-w-0 flex-1">
          <div
            ref={refs.title}
            className="relative min-h-[1.15em] min-w-0 text-(length:--presentation-title-size)"
          />
          <div
            ref={refs.meta}
            // Kept even when empty (a single page): the page name stays on the dashboard title's line.
            className="mt-1.5 flex min-h-6 min-w-0 items-center text-(length:--presentation-body-size) leading-6"
          />
        </div>
      </div>
      <div className="ml-auto flex min-w-0 max-w-full items-center gap-x-(--presentation-header-gap)">
        {controls}
        {logo}
      </div>
      {exit}
    </header>
  ) : (
    <header
      ref={headerRef}
      className="@container/pageheader sticky top-0 z-30 mx-(--presentation-safe-x) flex flex-wrap items-center justify-between gap-x-8 gap-y-3 bg-background pt-(--presentation-safe-top) pb-(--presentation-header-bottom)"
    >
      <div className="flex min-w-0 flex-1 items-center gap-4 wall-short:gap-3">
        {/* As tall as the title block: title and context lines, or the single line of a short screen. */}
        {titleVisual ? (
          <div
            data-header-image
            className="flex h-(--presentation-title-block) max-w-[calc(var(--presentation-title-block)*4)] shrink-0 items-center wall-short:h-[calc(var(--presentation-title-size)*1.2)]"
          >
            {titleVisual}
          </div>
        ) : null}
        {/* A short screen reads the title and its context on one line. */}
        <div className="min-w-0 flex-1 wall-short:grid wall-short:grid-cols-[auto_minmax(0,1fr)] wall-short:items-baseline wall-short:gap-x-5">
          <h1
            aria-busy={titleLoading || undefined}
            className="truncate text-(length:--presentation-title-size) leading-[1.2] font-semibold text-brand-navy wall-short:shrink-0 dark:text-white"
          >
            {titleText}
          </h1>
          <div
            className={cn(
              "mt-1.5 wall-short:mt-0 flex min-h-6 min-w-0 items-center gap-x-2 text-(length:--presentation-body-size) leading-6 text-secondary",
              separated,
            )}
          >
            {context}
          </div>
        </div>
      </div>
      <div className="ml-auto flex min-w-0 max-w-full">{controls}</div>
      {logo}
      {exit}
    </header>
  );

  return (
    <AppLayout viewportLocked tenantScopeRail={tenantScopeRail}>
      <PresentationHeaderContext value={headerContext}>
        <main
          ref={mainRef}
          id="main-content"
          tabIndex={-1}
          data-war-room-fullscreen
          data-page-identity={paged || undefined}
          data-pointer-active={exitVisible || undefined}
          className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto bg-background"
          onMouseMove={revealExit}
        >
          {header}
          <div
            ref={contentRef}
            className="min-w-0 px-(--presentation-safe-x) pt-(--presentation-content-top) pb-(--presentation-safe-bottom)"
          >
            {children}
          </div>
        </main>
      </PresentationHeaderContext>
    </AppLayout>
  );
}
